import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/daijrt3p14a1h3mr75hg';

type Status = 'idle' | 'loading' | 'success' | 'error';

const NewsletterForm = () => {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    data.delete('website_alt');

    setStatus('loading');
    setFormError('');

    try {
      const body = new URLSearchParams();
      data.forEach((value, key) => {
        if (typeof value === 'string') body.append(key, value);
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg
            ? serverMsg
            : '提交未成功，请稍后重试。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请检查连接后重试。');
    }
  };

  return (
    <div>
      <h3 className="font-heading text-lg font-semibold text-background-50">订阅最新热料</h3>
      <p className="mt-2 text-xs leading-relaxed text-secondary-200 md:text-sm">
        每周精选热门现场与话题解析，一封邮件看完，随时可以退订。
      </p>

      <form
        id="kanliao-newsletter-form"
        data-readdy-form
        action={SUBMIT_ADDR}
        method="post"
        onSubmit={handleSubmit}
        className="mt-4 flex flex-col gap-3 sm:flex-row"
      >
        <input
          type="email"
          name="email"
          required
          placeholder="你的邮箱"
          aria-label="订阅邮箱"
          className="w-full rounded-md border border-secondary-700 bg-secondary-800 px-3.5 py-2.5 text-sm text-background-50 placeholder:text-secondary-300 focus:outline-none focus:ring-2 focus:ring-accent-400"
        />
        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="field-trap-alt"
        />
        <select
          name="interest"
          aria-label="感兴趣的分类"
          defaultValue="hot"
          className="w-full rounded-md border border-secondary-700 bg-secondary-800 px-3 py-2.5 text-sm text-background-50 focus:outline-none focus:ring-2 focus:ring-accent-400 sm:w-40"
        >
          <option value="hot">热点突发</option>
          <option value="ent">娱乐圈</option>
          <option value="society">社会万象</option>
          <option value="tech">科技前沿</option>
          <option value="life">生活趣闻</option>
          <option value="talk">影视解说</option>
        </select>
        <button
          type="submit"
          disabled={status === 'loading'}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-label font-medium text-foreground-950 transition-colors hover:bg-accent-600 disabled:opacity-60"
        >
          {status === 'loading' ? '提交中…' : '立即订阅'}
        </button>
      </form>

      {status === 'success' && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-accent-300">
          <i className="ri-checkbox-circle-line" />
          订阅成功，感谢关注，我们会把最新热料发送到你的邮箱。
        </p>
      )}
      {status === 'error' && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-primary-300">
          <i className="ri-error-warning-line" />
          {formError}
        </p>
      )}
      {status !== 'success' && status !== 'error' && (
        <p className="mt-3 text-[11px] text-secondary-300">
          提交即表示同意我们仅将邮箱用于内容推送，不会用于其他用途。
        </p>
      )}
    </div>
  );
};

export default NewsletterForm;